import { booleanAttribute } from "#ce/utils/props";

export const NumberSliderVariant = Object.freeze({
  INPUT: "input",
  SLIDER_ONLY: "slider-only",
});

// `value` is absent on purpose: the element owns a live `value` property, which a declared prop
// would shadow with an accessor onto the prop store. The attribute seeds it and names the reset value.
export const props = Object.freeze({
  /**
   * Layout. "input" puts the label and a narrow numeric input in one row above the slider,
   * with the input at the end. "slider-only" drops the input and shows the current value at
   * the end of the label row.
   *
   * @type {String}
   * @default "input"
   * @values input, slider-only
   * @storybook-control select
   */
  variant: {
    type: String,
    default: NumberSliderVariant.INPUT,
    validator: (value) => Object.values(NumberSliderVariant).includes(value),
  },
  /**
   * Show the smallest and largest values under the ends of the track, in either variant.
   *
   * @type {Boolean|String}
   * @default false
   */
  showBounds: booleanAttribute(),
  /**
   * The selectable values. When set, the slider snaps through this list (and
   * typed values snap to the nearest entry) instead of stepping through the
   * min/max/step range. Accepts an array or, when set as an attribute, a JSON
   * array string (e.g. values="[5,10,25,50]").
   *
   * @type {Array|String}
   */
  values: {
    type: [Array, String],
    default: undefined,
  },
  /**
   * Smallest selectable value. Ignored when values is set.
   *
   * @type {Number|String}
   * @default 1
   * @storybook-control number
   */
  min: {
    type: [Number, String],
    default: 1,
  },
  /**
   * Largest selectable value. Ignored when values is set.
   *
   * @type {Number|String}
   * @default 150
   * @storybook-control number
   */
  max: {
    type: [Number, String],
    default: 150,
  },
  /**
   * Step increment between selectable values. Ignored when values is set.
   *
   * @type {Number|String}
   * @default 1
   * @storybook-control number
   */
  step: {
    type: [Number, String],
    default: 1,
  },
  /**
   * Label shown at the start of the row above the slider, omitted from the DOM when unset.
   * Doubles as the accessible name of the numeric input and the slider, so always provide
   * one — without it they are unnamed for assistive technology.
   *
   * @type {String}
   */
  label: {
    type: String,
    default: undefined,
  },
  /**
   * Formats the value for display and for screen readers, such as
   * `(value) => \`${value} miles\``. Set it as a property. Omitted, the number is shown as is.
   *
   * @type {Function}
   */
  formatValue: {
    type: Function,
    default: undefined,
  },
  /**
   * Stable data-testid for QA journeys, applied to the numeric input, or to the slider in
   * the "slider-only" variant.
   *
   * @type {String}
   */
  inputTestId: {
    type: String,
    default: undefined,
  },
  /**
   * Disable both the numeric input and the slider so they ignore input.
   *
   * @type {Boolean|String}
   * @default false
   */
  disabled: booleanAttribute(),
});
